<script setup>
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useModuleOptions } from '@/composables/useModuleOptions';
import { useToast } from '@/composables/useToast';
import {
  LEVEL_OPTIONS,
  NON_GRANTABLE_ROLE_CODES,
  RoleCode,
  parseRoleIds,
} from '@/constants/accounts';
import { required } from '@/utils/validation';

// Verify a pending registration, or change a verified user's level and
// designations (the roles that decide which pages they see).
const props = defineProps({
  account: { type: Object, default: null },
  mode: {
    type: String,
    default: 'verify',
    validator: (v) => ['verify', 'update'].includes(v),
  },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const toast = useToast();
const { findRoleId, findRoleCode, designationOptions, withoutImpliedTeacher, withImpliedTeacher } = useModuleOptions();

const formRef = ref(null);
const saving = shallowRef(false);
const form = reactive({ levelScope: null, designations: [] });

const isParent = computed(
  () => findRoleCode(props.account?.user_assignedModuleID) === RoleCode.Parent,
);
const title = computed(() =>
  props.mode === 'verify' ? 'Verify account' : 'Access',
);

watch(open, (isOpen) => {
  if (!isOpen || !props.account) return;
  const granted = parseRoleIds(props.account.user_subModules).filter(
    (id) => !NON_GRANTABLE_ROLE_CODES.includes(findRoleCode(id)),
  );
  form.levelScope = props.account.levelScope ?? null;
  // New faculty registrations start as Teacher.
  form.designations = withoutImpliedTeacher(granted.length ? granted : [findRoleId(RoleCode.Teacher)].filter(Boolean));
  formRef.value?.resetValidation();
});

// Picking Adviser drops the Subject Teacher chip: it's included (2 in 1).
watch(
  () => form.designations,
  (picked) => {
    const shown = withoutImpliedTeacher(picked);
    if (shown.length !== (picked || []).length) form.designations = shown;
  },
);

async function save() {
  if (!isParent.value) {
    const { valid } = await formRef.value.validate();
    if (!valid) return;
  }
  saving.value = true;
  try {
    const body = await apiRequest('post', '/user-details/updateVerifiedUser', {
      id: props.account.id,
      userID: props.account.user_id,
      subModules: JSON.stringify(withImpliedTeacher(form.designations)),
      levelScope: isParent.value ? null : form.levelScope,
      update_type: props.mode === 'verify' ? 1 : 2,
    });
    assertOk(body);
    toast.success(body.msg || 'Saved.');
    open.value = false;
    emit('saved');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the account.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="title"
    :description="account?.name"
    size="md"
  >
    <v-alert v-if="isParent" type="info" variant="tonal" density="compact">
      Parent accounts have fixed access to their children's records.
    </v-alert>

    <v-form v-else ref="formRef" class="form-stack" @submit.prevent="save">
      <UiFormField
        label="School level"
        description="JHS/SHS Admin and Prefect of Discipline roles set this automatically."
        required
        v-slot="{ id }"
      >
        <v-select
          :id="id"
          v-model="form.levelScope"
          :items="LEVEL_OPTIONS"
          item-value="value"
          item-title="title"
          placeholder="Select a level"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>

      <UiFormField
        label="Designations"
        description="What this user can access. Adviser includes Subject Teacher, and only advisers can be assigned to a section in Rooms."
        required
        v-slot="{ id }"
      >
        <v-autocomplete
          :id="id"
          v-model="form.designations"
          :items="designationOptions(form.designations)"
          item-value="id"
          item-title="description"
          placeholder="Select designations"
          :rules="[required]"
          multiple
          chips
          closable-chips
          hide-details="auto"
        />
      </UiFormField>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" @click="save">
        {{ mode === 'verify' ? 'Verify account' : 'Save changes' }}
      </v-btn>
    </template>
  </UiDialog>
</template>
